import i18n from '@/i18n/i18n';
import store from '@/store';
import Http from '@/services/http';
import AuthenticationService from '@/services/authentication';
import {
  MyData,
  ChangePasswordRequest,
  ChangeTemporaryPasswordRequest,
  UsersPagedRequest,
  UserListPagedResponse,
  ResetPasswordResponse,
  User,
  CurrentUserProfilesRequest,
  UserProfilesLookup,
  CreatedUser,
} from 'sigma-mappings';
import { timezones } from '@/constants/localizer';

const DEFAULT_COUNTRY = 'BR';

const urls = {
  base: '/usuario',
  myData: '/usuario/meusdados',
  changePassword: '/usuario/meusdados/senha',
  changeTemporaryPassword: 'usuario/primeiroacesso/senha',
  changeSecretQuestion: '/perguntasecreta',
  getList: '/Usuario/listagem',
  blockUser: id => `/Usuario/bloquear/${id}`,
  unblockUser: id => `/Usuario/desbloquear/${id}`,
  get: id => `${urls.base}/${id}`,
  delete: id => `${urls.base}/${id}`,
  resetPassword: (id, sendEmail = false) => `/Usuario/gerenciamento/senha/redefinir/${id}?enviarEmailSenha=${sendEmail}`,
  currentUserProfiles: '/usuario/perfil',
  userProfile: id => `/usuario/${id}/perfil`,
  availableEmailCheck: email => `/usuario/emaildisponivel/${email}`,
  availableUsernameCheck: username => `/usuario/nomeusuariodisponivel/${username}`,
  usersUnderAdministration: '/usuario/lookup',
};

function catchChangePasswordOrSecretQuestion(error) {
  const { totalDeTentativas: totalAttempts = 0 } = error.response?.data?.parametros;
  const { tentativasRestantes: remainingAttempts = 0 } = error.response?.data?.parametros;
  const blockedUser = error.response?.data?.codigoMensagem === 'ME004' ?? false;

  if (blockedUser) {
    AuthenticationService.redirectToLogin(false);
  }

  if (totalAttempts) {
    return Promise.reject(Object.assign(new Error(), {
      totalAttempts,
      remainingAttempts,
      blockedUser,
    }));
  }
  return Promise.reject(error);
}

function getMessageParams(error) {
  const { username } = store.getters.user;
  const baseMessageParams = {
    username,
  };
  const remainingAttempts = error.response?.data?.parametros?.tentativasRestantes || 0;
  const failedAttempts = error.response?.data?.parametros?.tentativasDeAcessoComFalha || 0;
  const blockedUser = error.response?.data?.codigoMensagem === 'ME004' ?? false;

  return {
    ...baseMessageParams,
    remainingAttempts,
    failedAttempts,
    blockedUser,
  };
}

function getDeleteUserErrorParams(username, error) {
  if (error.response?.data?.parametros?.perfil) {
    return {
      username,
      Perfis: error.response.data.parametros.perfil.join(', '),
    };
  }
  return null;
}

function resolveError(error) {
  if (error.response.status === 400) {
    return false;
  }

  return Promise.reject(error);
}

export default {

  getMyData() {
    const http = new Http();
    return http.get(urls.myData).then(response => new MyData(response.data));
  },

  saveMyData(data) {
    const http = new Http();
    const apiModel = new MyData.MapBack(data);
    return http.put(urls.myData, apiModel);
  },

  changePassword(oldPassword, newPassword) {
    const http = new Http({
      errorHandling: {
        responseMessageParams: getMessageParams,
      },
    });
    return http.put(urls.changePassword, new ChangePasswordRequest({
      oldPassword,
      newPassword,
    })).catch(catchChangePasswordOrSecretQuestion);
  },

  changeTemporaryPassword(newPassword) {
    const http = new Http();
    return http.put(urls.changeTemporaryPassword, new ChangeTemporaryPasswordRequest({
      newPassword,
    }));
  },

  changeSecretQuestion(password, newQuestion, newAnswer) {
    const http = new Http({
      errorHandling: {
        responseMessageParams: getMessageParams,
      },
    });
    return http.put(urls.changeSecretQuestion, {
      senha: password,
      pergunta: newQuestion,
      resposta: newAnswer,
    }).catch(catchChangePasswordOrSecretQuestion);
  },

  getUsers(requestFilter) {
    const http = new Http();
    return http.post(urls.getList, new UsersPagedRequest(requestFilter))
      .then(({ data }) => new UserListPagedResponse(data));
  },

  deleteUser({ id, username, authenticatedUserPassword }) {
    const http = new Http({
      errorHandling: {
        responseMessageParams: error => getDeleteUserErrorParams(username, error),
      },
    });
    return http.delete(urls.delete(id), {
      headers: {
        'User-Password': authenticatedUserPassword,
      },
    });
  },

  resetPassword(userId, authenticatedUserPassword, sendEmail = false) {
    const http = new Http();
    return http.put(urls.resetPassword(userId, sendEmail), null, {
      headers: {
        'User-Password': authenticatedUserPassword,
      },
    }).then(response => new ResetPasswordResponse(response.data));
  },

  getNewUser() {
    const newUser = new User({
      idioma: i18n.defaultNavigatorLanguage,
      codigoPais: DEFAULT_COUNTRY,
      perfis: [],
      permissoes: [],
      timeZoneId: timezones.DEFAULT,
    });
    return Promise.resolve(newUser);
  },

  getUser(userId) {
    const http = new Http();
    return http.get(urls.get(userId))
      .then(({ data }) => new User(data));
  },

  isEmailAvailable(email) {
    const http = new Http({
      errorHandling: {
        ignore: error => error.response && error.response.status === 400,
      },
    });
    return http.get(urls.availableEmailCheck(email)).then(() => true)
      .catch(error => resolveError(error));
  },

  isUsernameAvailable(username) {
    const http = new Http({
      errorHandling: {
        ignore: error => error.response && error.response.status === 400,
      },
    });
    return http.get(urls.availableUsernameCheck(username)).then(() => true)
      .catch(error => resolveError(error));
  },

  saveUser(data) {
    const http = new Http();
    const apiData = new User.MapBack(data);
    return data.id == null
      ? http.post(urls.base, apiData).then(response => new CreatedUser(response.data))
      : http.put(urls.base, apiData);
  },

  blockUser(userId, authenticatedUserPassword) {
    const http = new Http();
    return http.post(urls.blockUser(userId), null, {
      headers: {
        'User-Password': authenticatedUserPassword,
      },
    });
  },

  unblockUser(userId, authenticatedUserPassword) {
    const http = new Http();
    return http.post(urls.unblockUser(userId), null, {
      headers: {
        'User-Password': authenticatedUserPassword,
      },
    });
  },

  getProfilesManagedByCurrentUser() {
    const http = new Http();
    return http.get(urls.currentUserProfiles, {
      params: new CurrentUserProfilesRequest({
        onlyManaged: true,
      }),
    }).then(({ data }) => data.map(item => new UserProfilesLookup(item)));
  },

  getUserProfiles(userId) {
    const http = new Http();
    return http.get(urls.userProfile(userId))
      .then(({ data }) => data.map(item => new UserProfilesLookup(item)));
  },

  async getProfilesForUserEditing(userId, alreadyLinkedProfiles) {
    const profilesToShow = await this.getProfilesManagedByCurrentUser();

    profilesToShow.forEach((profile) => {
      // eslint-disable-next-line no-param-reassign
      profile.accessibleByCurrentUser = true;
    });

    if (userId) {
      if (!alreadyLinkedProfiles.every(alp => profilesToShow.some(cup => cup.id === alp))) {
        const userProfiles = await this.getUserProfiles(userId);
        userProfiles
          .filter(profile => !profilesToShow.some(cup => cup.id === profile.id))
          .forEach(profile => profilesToShow.push({
            accessibleByCurrentUser: false,
            ...profile,
          }));
      }
    }
    return profilesToShow;
  },
  getUsersUnderAdministration() {
    const http = new Http();
    return http.get(urls.usersUnderAdministration)
      .then(({ data }) => data.map(item => new UserProfilesLookup(item)));
  },
};
